import {
	Avatar,
	Box,
	Button,
	Checkbox,
	Flex,
	Input,
	Menu,
	MenuButton,
	MenuList,
	Text,
	useDisclosure,
} from '@chakra-ui/react'
import { GetIcon, Icons } from '@ds/Icons'
import { DDSTheme, ddsTokens } from '@ds/index'
import { RADII } from '@ds/tokens/legacy/borders'
import { COLORS } from '@ds/tokens/legacy/colors'
import { useCallback, useMemo, useState } from 'react'

import { TEXT_ENUM } from '@/styles/components/text'

interface SelectOption {
	label: string
	value: string
	avatarUrl?: string | null
}

interface SelectMenuProps {
	options: SelectOption[]
	selectedValues: string[]
	onChange: (selected: SelectOption[]) => void
	placeholder: string
	maxSelections?: number
	isLoading?: boolean
	width?: string | number
}

export const SelectMenu: React.FC<SelectMenuProps> = ({
	options,
	selectedValues,
	onChange,
	placeholder,
	maxSelections = Infinity,
	isLoading = false,
	width = '100%',
}) => {
	const { isOpen, onOpen, onClose } = useDisclosure()
	const [searchQuery, setSearchQuery] = useState('')
	const tokens = ddsTokens[DDSTheme.light]

	const filteredOptions = useMemo(() => {
		if (!searchQuery.trim()) return options
		return options.filter((option) =>
			option.label.toLowerCase().includes(searchQuery.toLowerCase()),
		)
	}, [options, searchQuery])

	const sortedOptions = useMemo(() => {
		return [...filteredOptions].sort((a, b) => {
			const aSelected = selectedValues.includes(a.value)
			const bSelected = selectedValues.includes(b.value)
			if (aSelected && !bSelected) return -1
			if (!aSelected && bSelected) return 1
			return 0
		})
	}, [filteredOptions, selectedValues])

	const toggleFormSelection = useCallback(
		(formId: string, isSelected: boolean) => {
			const newSelectedValues = isSelected
				? [...selectedValues, formId]
				: selectedValues.filter((id) => id !== formId)

			const newSelectedOptions = options.filter((option) =>
				newSelectedValues.includes(option.value),
			)

			if (isSelected && newSelectedOptions.length > maxSelections) {
				return
			}

			onChange(newSelectedOptions)
		},
		[selectedValues, options, onChange, maxSelections],
	)

	return (
		<Menu
			isOpen={isOpen}
			onClose={onClose}
			onOpen={onOpen}
			closeOnSelect={false}
		>
			<MenuButton
				as={Button}
				w={width}
				borderRadius="md"
				border={`1px solid ${tokens.primitive.light.gray[300]}`}
				bg="white"
				justifyContent="space-between"
				rightIcon={
					<GetIcon
						icon={isOpen ? Icons.chevron_up : Icons.chevron_down}
						boxSize={4}
						color={tokens.primitive.light.gray[500]}
					/>
				}
				_hover={{ bg: 'white' }}
				_active={{ bg: 'white' }}
			>
				<Text
					color={
						selectedValues.length ? 'black' : tokens.primitive.light.gray[400]
					}
					isTruncated
				>
					{selectedValues.length
						? `${selectedValues.length} selected`
						: placeholder}
				</Text>
			</MenuButton>
			<MenuList
				width="300px"
				py={0}
				px={0}
				maxH="420px"
				border={`1px solid ${tokens.primitive.light.gray[300]}`}
			>
				<Box
					p={2}
					px={0}
					bg={tokens.primitive.light.gray[25]}
					onClick={(e) => e.stopPropagation()}
				>
					<Input
						variant="unstyled"
						w="full"
						placeholder="Search"
						value={searchQuery}
						onChange={(e) => setSearchQuery(e.target.value)}
						pl={3}
						onClick={(e) => e.stopPropagation()}
					/>
				</Box>
				<Box maxH="350px" overflowY="auto" p={1}>
					{isLoading ? (
						<Flex justify="center" align="center" py={4}>
							<Text>Loading...</Text>
						</Flex>
					) : sortedOptions.length > 0 ? (
						sortedOptions.map((option) => (
							<Flex
								key={option.value}
								px={2}
								py={2.5}
								alignItems="center"
								_hover={{ bg: COLORS.purple[2] }}
								borderRadius={RADII.md}
								cursor="pointer"
								{...(selectedValues.length >= maxSelections &&
									!selectedValues.includes(option.value) && {
										opacity: 0.5,
										cursor: 'not-allowed',
									})}
								onClick={(e) => {
									e.preventDefault()
									e.stopPropagation()
									toggleFormSelection(
										option.value,
										!selectedValues.includes(option.value),
									)
								}}
							>
								<Checkbox
									isChecked={selectedValues.includes(option.value)}
									mr={2}
									disabled={
										selectedValues.length >= maxSelections &&
										!selectedValues.includes(option.value)
									}
								>
									<Flex alignItems="center" gap={2}>
										<Avatar
											src={option.avatarUrl || ''}
											name={option.label}
											size="sm"
										/>
										<Text variant={TEXT_ENUM.medium14}>{option.label}</Text>
									</Flex>
								</Checkbox>
							</Flex>
						))
					) : (
						<Flex
							justifyContent="center"
							alignItems="center"
							py={4}
							color={COLORS.gray[7]}
						>
							<Text variant={TEXT_ENUM.regular14}>No options found</Text>
						</Flex>
					)}
				</Box>
			</MenuList>
		</Menu>
	)
}
